"use client";

import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getBannerList } from "@/store/controller/banner.controller";
import { getPpdbList } from "@/store/controller/ppdb.controller";

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace("#", "");
  const value = parseInt(clean, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function lightenChannel(channel: number, amount: number): number {
  return Math.min(255, Math.round(channel + (255 - channel) * amount));
}

function buildOverlayGradient(overlayColor: string | null | undefined): string {
  const safeHex = overlayColor && /^#[0-9a-fA-F]{6}$/.test(overlayColor) ? overlayColor : "#0d4d0d";
  const { r, g, b } = hexToRgb(safeHex);
  const lr = lightenChannel(r, 0.2);
  const lg = lightenChannel(g, 0.2);
  const lb = lightenChannel(b, 0.2);

  return `linear-gradient(to right, rgba(${r},${g},${b},0.97) 0%, rgba(${lr},${lg},${lb},0.88) 38%, rgba(${lr},${lg},${lb},0.4) 62%, transparent 100%)`;
}

export function HeroSection() {
  const dispatch = useAppDispatch();
  const banners = useAppSelector((state) => state.banner.list);
  const ppdb = useAppSelector((state) => state.ppdb.list[0]);
  const [current, setCurrent] = useState(0);
  const total = banners.length;

  const ppdbCtaLabel = ppdb?.cta_label || "Pendaftaran PPDB";
  const ppdbCtaHref = ppdb?.cta_href || "#ppdb";

  useEffect(() => {
    dispatch(getBannerList({}));
    dispatch(getPpdbList({}));
  }, [dispatch]);

  useEffect(() => {
    if (total === 0) return;
    const timer = setInterval(() => setCurrent((previousSlide) => (previousSlide + 1) % total), 5000);
    return () => clearInterval(timer);
  }, [total]);

  const prev = () => setCurrent((previousSlide) => (previousSlide - 1 + total) % total);
  const next = () => setCurrent((previousSlide) => (previousSlide + 1) % total);

  if (total === 0) {
    return <section id="beranda" className="relative w-full pt-24 sm:pt-28 min-h-[520px]" />;
  }

  return (
    <section id="beranda" className="relative w-full pt-24 sm:pt-28 overflow-hidden">
      <div className="grid grid-cols-1 min-h-[560px] sm:min-h-[620px] lg:min-h-[680px]">
        {banners.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className="[grid-area:1/1] relative transition-opacity duration-700"
            style={{
              opacity: slideIndex === current ? 1 : 0,
              zIndex: slideIndex === current ? 1 : 0,
              pointerEvents: slideIndex === current ? "auto" : "none",
            }}
            aria-hidden={slideIndex !== current}
          >
            <img
              src={slide.image}
              alt={slide.description}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />

            <div
              className="absolute inset-0"
              style={{ background: buildOverlayGradient(slide.overlay_color) }}
            />

            <div className="relative py-14 sm:py-16 lg:py-20 pb-24 sm:pb-24">
              <div className="w-full max-w-7xl mx-auto px-8 sm:px-12">
                <div className="max-w-lg">
                  <span className="inline-block text-yellow-400 font-semibold text-sm uppercase tracking-widest mb-4">
                    {slide.type}
                  </span>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5 whitespace-pre-line">
                    {slide.description}
                  </h1>
                  <p className="text-white/75 text-sm sm:text-base leading-relaxed mb-8 max-w-sm">{slide.content}</p>
                  <div className="flex flex-wrap gap-3">
                    {slide.cta && (
                      <a
                        href={slide.cta_href ?? "#"}
                        className="inline-flex items-center gap-2 bg-white text-green-800 hover:bg-yellow-400 hover:text-green-900 font-bold px-6 py-3 rounded-lg transition-all text-sm shadow-lg"
                      >
                        {slide.cta}
                        <ChevronRight size={16} />
                      </a>
                    )}
                    <a
                      href={ppdbCtaHref}
                      className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white/15 font-semibold px-6 py-3 rounded-lg transition-all text-sm"
                    >
                      {ppdbCtaLabel}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 flex items-center gap-4">
          <div className="flex gap-2">
            {banners.map((_, dotIndex) => (
              <button
                key={dotIndex}
                onClick={() => setCurrent(dotIndex)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  dotIndex === current ? "w-8 bg-yellow-400" : "w-4 bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2 ml-4">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 border border-white/25 flex items-center justify-center text-white transition-all"
            >
              <ChevronRight size={16} className="rotate-180" />
            </button>
            <button
              onClick={next}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 border border-white/25 flex items-center justify-center text-white transition-all"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <span className="ml-auto text-white/50 text-xs font-medium tabular-nums">
            {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
